// ===================================
// TRENCH QUEST - Context Builder
// Builds full GameMasterContext from game state
// Includes portfolio awareness for AI-driven narratives
// ===================================

import type {
  GameSession,
  GameMasterContext,
  PlayerAction,
  RealMarketData,
  NewsItem,
  ConsequenceLog,
  GeneratedSituation,
} from '@/types'
import { getRealMarketContext, getCryptoNews, checkPortfolioAlerts } from '@/services/marketData'

// No hard cap on decisions - games end organically through:
// - Hitting goal target (win)
// - Going bankrupt (lose)
// - Death or imprisonment life events (lose)
// Risk escalates gradually based on decision count and progress

/**
 * Portfolio position info for AI context
 */
export interface PortfolioPosition {
  symbol: string
  name: string
  allocation: number       // % of portfolio
  pnlPercent: number      // P&L %
  holdingHours: number    // How long held
  isSignificant: boolean  // >20% of portfolio
  isMajor: boolean        // >50% of portfolio
  isUnderwater: boolean   // Negative P&L
  isProfitable: boolean   // Positive P&L
}

/**
 * Recent trade info for AI context
 */
export interface RecentTradeInfo {
  symbol: string
  action: 'buy' | 'sell'
  amount: number
  price: number
  hoursAgo: number
  pnl?: number            // For sells
}

/**
 * Build detailed portfolio context for AI prompts
 */
export function getPortfolioContext(session: GameSession): string {
  const { holdings, cashBalance, totalValue } = session.player.portfolio

  // Filter out USDC from holdings analysis
  const tokenHoldings = holdings.filter(h => h.symbol !== 'USDC')

  if (tokenHoldings.length === 0) {
    const cashPercent = ((cashBalance / totalValue) * 100).toFixed(1)
    return `
## PLAYER PORTFOLIO STATE

Player is ${cashPercent}% in cash (USDC), no token positions.
This creates FOMO opportunities - they're sitting on the sidelines while markets move.
Consider situations that tempt them to take positions.
`
  }

  const positions: PortfolioPosition[] = tokenHoldings.map(h => {
    const value = h.amount * h.currentPrice
    const allocation = (value / totalValue) * 100
    const pnlPercent = ((h.currentPrice - h.avgBuyPrice) / h.avgBuyPrice) * 100

    // Estimate holding time (simplified - would need trade timestamps)
    const holdingHours = session.gameTime.totalGameHours // Approximation

    return {
      symbol: h.symbol,
      name: h.name || h.symbol,
      allocation,
      pnlPercent,
      holdingHours,
      isSignificant: allocation > 20,
      isMajor: allocation > 50,
      isUnderwater: pnlPercent < 0,
      isProfitable: pnlPercent > 0,
    }
  })

  // Sort by allocation
  positions.sort((a, b) => b.allocation - a.allocation)

  const cashAllocation = (cashBalance / totalValue) * 100
  const significantPositions = positions.filter(p => p.isSignificant)
  const underwaterPositions = positions.filter(p => p.isUnderwater)
  const profitablePositions = positions.filter(p => p.isProfitable && p.pnlPercent > 10)

  let context = `
## PLAYER PORTFOLIO STATE

`

  // Position summary
  context += `**Current Positions:**
${positions.map(p => {
  const pnlSign = p.pnlPercent >= 0 ? '+' : ''
  const status = p.isMajor ? ' [MAJOR POSITION - CREATE DRAMA HERE]' :
                 p.isSignificant ? ' [SIGNIFICANT]' : ''
  return `- ${p.symbol}: ${p.allocation.toFixed(1)}% of portfolio, ${pnlSign}${p.pnlPercent.toFixed(1)}% P&L${status}`
}).join('\n')}
Cash: $${cashBalance.toLocaleString()} (${cashAllocation.toFixed(1)}%)

`

  // AI guidance based on portfolio state
  context += `**PORTFOLIO-AWARE NARRATIVE GUIDANCE:**

`

  if (significantPositions.length > 0) {
    const major = significantPositions[0]
    if (major) {
      context += `- Player has ${major.allocation.toFixed(0)}% in ${major.symbol}. CREATE SITUATIONS ABOUT THIS TOKEN!
  - Insider info, whale activity, news, or rumors about ${major.symbol}
  - Don't always be negative - positive surprises create engagement too
  - Their ${major.symbol} position = built-in dramatic tension

`
    }
  }

  if (underwaterPositions.length > 0) {
    const worst = underwaterPositions.sort((a, b) => a.pnlPercent - b.pnlPercent)[0]
    if (worst) {
      context += `- Player is underwater on ${worst.symbol} (${worst.pnlPercent.toFixed(1)}%)
  - Create hope/recovery situations or test their conviction
  - "Diamond hands or paper hands?" moments

`
    }
  }

  if (profitablePositions.length > 0) {
    const best = profitablePositions[0]
    if (best) {
      context += `- Player is up ${best.pnlPercent.toFixed(1)}% on ${best.symbol}
  - Create "take profit or let it ride" dilemmas
  - Greed vs wisdom tension

`
    }
  }

  if (cashAllocation > 70) {
    context += `- Player is mostly in cash (${cashAllocation.toFixed(0)}%)
  - Create FOMO-inducing situations (missed pumps, alpha opportunities)
  - Push toward taking positions

`
  }

  return context
}

/**
 * Build game progress context for AI - pacing and urgency
 * Games end organically - no hard cap on decisions
 */
export function getGameProgressContext(session: GameSession): string {
  const decisionCount = session.decisionCount || 0
  const portfolioValue = session.player.portfolio.totalValue
  const targetAmount = session.goal.targetAmount
  const progressPercent = (portfolioValue / targetAmount) * 100

  let pacingGuidance: string

  // Pacing escalates based on progress and decision count
  // Early: decisions 1-5, Mid: decisions 6-10, Late: 11+
  // Risk also escalates as player gets closer to or further from goal

  if (decisionCount <= 5) {
    pacingGuidance = `
## GAME PACING: EARLY GAME (Decision ${decisionCount + 1})

This is early game. Set the stage but keep stakes moderate.
- Introduce opportunities and characters
- Player is finding their footing
- Alpha hints should be promising but not guaranteed
- Market effects: ±10-30% typical
- NO game-ending events unless player is truly reckless
`
  } else if (decisionCount <= 10) {
    pacingGuidance = `
## GAME PACING: MID GAME (Decision ${decisionCount + 1})

Stakes are rising. Player should feel the pressure building.
- Introduce bigger opportunities with real risk
- Consequences from earlier decisions can resurface
- Alpha quality improving - real 2-5x opportunities
- Market effects: ±20-50% possible
- Game-ending events RARE - only for truly extreme actions
`
  } else {
    pacingGuidance = `
## GAME PACING: LATE GAME (Decision ${decisionCount + 1})

The player has been in the trenches a while. Stakes should be high.
- High-stakes situations common
- Major opportunities and risks
- 5x-20x potential swings
- Consequences have weight
- Market effects: ±30-80% on risky plays
- Game-ending events possible for YOLO/reckless actions
`
  }

  // Add progress context - drives risk escalation organically
  let progressContext = ''
  if (progressPercent >= 100) {
    progressContext = `
**TARGET REACHED!** Player has hit $${portfolioValue.toLocaleString()} (target: $${targetAmount.toLocaleString()})
Focus on victory lap or "one more trade" temptation that could lose it all.
They can WIN if they stop now, or risk it all for more glory.
`
  } else if (progressPercent >= 70) {
    progressContext = `
**CLOSE TO WINNING:** ${progressPercent.toFixed(0)}% of target.
Need $${(targetAmount - portfolioValue).toLocaleString()} more.
Create "secure the win" vs "swing for glory" tension.
Gradually push toward situations that could seal the deal or set them back.
`
  } else if (progressPercent >= 40) {
    progressContext = `
**ON TRACK:** ${progressPercent.toFixed(0)}% of target.
Need solid gains to reach the goal.
Mix of medium and high-risk opportunities.
Push toward bigger plays but don't force recklessness.
`
  } else if (progressPercent >= 20) {
    progressContext = `
**BEHIND:** Only ${progressPercent.toFixed(0)}% of target!
Need significant gains to have a chance.
Create high-upside opportunities that could turn it around.
Make safe choices feel boring/unrewarding.
`
  } else {
    progressContext = `
**DANGER ZONE:** Only ${progressPercent.toFixed(0)}% of target!
Needs major gains to recover. Focus on high-risk/high-reward opportunities.
Safe plays should feel pointless at this stage.
`
  }

  return pacingGuidance + progressContext
}

/**
 * Get recent trades for AI context
 */
export function getRecentTradesContext(session: GameSession): string {
  const recentTrades = session.tradeHistory.slice(-5)

  if (recentTrades.length === 0) {
    return ''
  }

  const tradeLines = recentTrades.map(t => {
    const action = t.type === 'buy' ? 'Bought' : 'Sold'
    return `- ${action} ${t.toToken || t.fromToken} at $${t.price.toFixed(4)}`
  })

  return `
## RECENT TRADES (Reference for narrative continuity)

${tradeLines.join('\n')}

Consider referencing recent trades in situations:
- "Your recent buy caught someone's attention..."
- "Word of your trade spread..."
`
}

/**
 * Default values for when data is unavailable
 */
const DEFAULT_MARKET_DATA: RealMarketData = {
  topGainers: [],
  topLosers: [],
  trendingTokens: [],
  btcPrice: 95000,
  ethPrice: 3200,
  solPrice: 180,
  marketSentiment: 'neutral',
  fearGreedIndex: 50,
  totalMarketCap: 3000000000000,
  last24hVolume: 100000000000,
  lastUpdated: new Date(),
}

/**
 * Determine desired tone based on game state
 * No hard cap - tone escalates based on decision count and progress
 */
function determineTone(
  session: GameSession,
  marketData: RealMarketData
): GameMasterContext['desiredTone'] {
  const { portfolio } = session.player
  const progressPercent = (portfolio.totalValue / session.goal.targetAmount) * 100
  const decisionCount = session.decisionCount || 0

  // Late game (10+ decisions) - more dramatic/tense
  if (decisionCount >= 12) {
    return 'dramatic'
  }

  if (decisionCount >= 10) {
    return 'tense'
  }

  // Near goal - dramatic
  if (progressPercent >= 80) {
    return 'dramatic'
  }

  // Doing poorly - tense
  if (progressPercent <= 20) {
    return 'tense'
  }

  // Market extreme fear - tense
  if (marketData.marketSentiment === 'extreme_fear') {
    return 'tense'
  }

  // Market extreme greed - comedic (bubble vibes)
  if (marketData.marketSentiment === 'extreme_greed') {
    return 'comedic'
  }

  // Default - varies based on recent performance
  const recentPnL = calculateRecentPnL(session)
  if (recentPnL > 20) return 'comedic'
  if (recentPnL < -20) return 'tense'

  return 'chill'
}

/**
 * Calculate rough P&L from recent holdings
 */
function calculateRecentPnL(session: GameSession): number {
  const { holdings } = session.player.portfolio
  if (holdings.length === 0) return 0

  let totalPnL = 0
  let totalValue = 0

  for (const holding of holdings) {
    const value = holding.amount * holding.currentPrice
    const cost = holding.amount * holding.avgBuyPrice
    totalPnL += value - cost
    totalValue += cost
  }

  if (totalValue === 0) return 0
  return (totalPnL / totalValue) * 100
}

/**
 * Determine situation types to weight based on context
 * No hard cap - escalates organically based on decision count and progress
 */
function determineSituationTypes(
  session: GameSession,
  marketData: RealMarketData,
  recentActions: PlayerAction[]
): string[] {
  const types: string[] = []
  const { portfolio, traits } = session.player
  const progressPercent = (portfolio.totalValue / session.goal.targetAmount) * 100
  const decisionCount = session.decisionCount || 0

  // Late game (12+ decisions) - prioritize high-stakes situations
  if (decisionCount >= 12) {
    types.push('insider', 'power_play', 'crisis')
    // Still allow some variety
  } else if (decisionCount >= 8) {
    // Mid-late game: escalating stakes
    types.push('insider', 'power_play')
    if (progressPercent < 50) {
      types.push('crisis') // Behind? Add crisis for drama
    }
  }

  // Early game (< 6 decisions) - include randomness
  if (decisionCount < 6) {
    types.push('random')
  }

  // Near goal - more power plays and lifestyle
  if (progressPercent >= 70) {
    types.push('power_play', 'lifestyle')
  }

  // Low progress - more insider and crisis
  if (progressPercent <= 30) {
    types.push('insider', 'crisis')
  }

  // High degen score - risky opportunities
  if (traits.degenScore >= 60) {
    types.push('insider', 'power_play')
  }

  // High trust score - social and romantic
  if (traits.trustScore >= 60) {
    types.push('romance', 'insider')
  }

  // Market is pumping - lifestyle and power
  if (marketData.marketSentiment === 'extreme_greed' || marketData.marketSentiment === 'greed') {
    types.push('lifestyle', 'power_play')
  }

  // Market is dumping - crisis and recovery
  if (marketData.marketSentiment === 'extreme_fear' || marketData.marketSentiment === 'fear') {
    types.push('crisis', 'recovery')
  }

  // Recent trading activity - market events
  const recentTrades = recentActions.filter(a => a.type === 'trade').length
  if (recentTrades >= 3) {
    types.push('market')
  }

  // Been a while since social - add romance (only early/mid game, first 10 decisions)
  if (decisionCount < 10) {
    const recentSocial = recentActions.filter(a =>
      a.type === 'encounter' || a.type === 'choice'
    ).length
    if (recentSocial === 0) {
      types.push('romance', 'recovery')
    }
  }

  return [...new Set(types)] // Remove duplicates
}

/**
 * Build full GameMasterContext from current game state
 */
export async function buildGameMasterContext(
  session: GameSession,
  recentActions: PlayerAction[] = [],
  _consequenceLog: ConsequenceLog[] = [],
  excludeSituationIds: string[] = []
): Promise<GameMasterContext & { portfolioContext: string; recentTradesContext: string; gameProgressContext: string }> {
  // Fetch real market data
  let realMarketData: RealMarketData
  let cryptoNews: NewsItem[]

  try {
    [realMarketData, cryptoNews] = await Promise.all([
      getRealMarketContext(),
      getCryptoNews(),
    ])
  } catch (error) {
    console.error('Failed to fetch real market data:', error)
    realMarketData = DEFAULT_MARKET_DATA
    cryptoNews = []
  }

  // Determine tone and situation types based on context
  const desiredTone = determineTone(session, realMarketData)
  const situationTypes = determineSituationTypes(session, realMarketData, recentActions)

  // Build portfolio-aware context
  const portfolioContext = getPortfolioContext(session)
  const recentTradesContext = getRecentTradesContext(session)
  const gameProgressContext = getGameProgressContext(session)

  return {
    session,
    recentActions: recentActions.slice(0, 10), // Last 10 actions
    currentLocation: session.world.currentLocation,
    realMarketData,
    cryptoNews,
    currentDate: new Date(),
    desiredTone,
    situationTypes,
    excludeSituationIds,
    portfolioContext,
    recentTradesContext,
    gameProgressContext,
  }
}

/**
 * Check if we should generate a new situation based on triggers
 */
export async function checkSituationTriggers(
  session: GameSession,
  recentActions: PlayerAction[],
  lastSituationTime: Date | null
): Promise<{
  shouldGenerate: boolean
  trigger: 'player_action' | 'time_skip' | 'travel' | 'market_event' | 'manual' | 'follow_up' | null
  marketAlert?: { token: string; change: number; direction: 'up' | 'down' }
}> {
  const now = new Date()

  // No situation yet - generate one
  if (!lastSituationTime) {
    return { shouldGenerate: true, trigger: 'manual' }
  }

  // Check for significant market movement on held tokens
  const heldTokens = session.player.portfolio.holdings.map(h => h.symbol)
  if (heldTokens.length > 0) {
    try {
      const alerts = await checkPortfolioAlerts(heldTokens)
      if (alerts.length > 0) {
        return {
          shouldGenerate: true,
          trigger: 'market_event',
          marketAlert: alerts[0],
        }
      }
    } catch (error) {
      console.error('Failed to check portfolio alerts:', error)
    }
  }

  // Check time since last situation (minimum 1 game hour)
  const hoursSinceLastSituation =
    (now.getTime() - lastSituationTime.getTime()) / (1000 * 60 * 60)

  // Real-time check: if more than 5 minutes passed, might be time for something
  if (hoursSinceLastSituation >= 0.083) { // ~5 minutes
    // Check if player did a significant action
    const recentSignificantActions = recentActions.filter(a =>
      a.type === 'trade' ||
      a.type === 'choice' ||
      a.type === 'travel'
    )

    if (recentSignificantActions.length > 0) {
      const mostRecent = recentSignificantActions[0]

      if (mostRecent && mostRecent.type === 'travel') {
        return { shouldGenerate: true, trigger: 'travel' }
      }

      if (mostRecent && mostRecent.type === 'choice') {
        return { shouldGenerate: true, trigger: 'player_action' }
      }

      // Trade completed - small chance of follow-up
      if (mostRecent && mostRecent.type === 'trade' && Math.random() < 0.3) {
        return { shouldGenerate: true, trigger: 'player_action' }
      }
    }
  }

  // Been idle for a while (15+ minutes real time) - time skip scenario
  if (hoursSinceLastSituation >= 0.25) {
    return { shouldGenerate: true, trigger: 'time_skip' }
  }

  return { shouldGenerate: false, trigger: null }
}

/**
 * Convert player action history to PlayerAction format
 */
export function convertToPlayerActions(
  trades: { id: string; type: string; timestamp: Date; narrative: string }[],
  events: { id: string; type: string; timestamp: Date; title: string }[]
): PlayerAction[] {
  const actions: PlayerAction[] = []

  // Convert trades
  for (const trade of trades) {
    actions.push({
      id: trade.id,
      type: 'trade',
      description: trade.narrative || `Executed ${trade.type}`,
      timestamp: trade.timestamp,
    })
  }

  // Convert events
  for (const event of events) {
    actions.push({
      id: event.id,
      type: event.type === 'encounter' ? 'encounter' : 'choice',
      description: event.title,
      timestamp: event.timestamp,
    })
  }

  // Sort by timestamp, most recent first
  actions.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())

  return actions
}

/**
 * Create PlayerAction from a choice resolution
 */
export function createChoiceAction(
  situation: GeneratedSituation,
  choiceId: string,
  outcome: string
): PlayerAction {
  const choice = situation.choices?.find(c => c.id === choiceId)

  return {
    id: `action-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
    type: 'choice',
    description: choice?.text || 'Made a choice',
    timestamp: new Date(),
    situationId: situation.id,
    choiceId,
    outcome,
  }
}

/**
 * Create ConsequenceLog entry from situation outcome
 */
export function createConsequenceLog(
  situationId: string,
  choiceId: string,
  flags: string[],
  summary: string,
  characters: string[]
): ConsequenceLog {
  return {
    id: `conseq-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
    situationId,
    choiceId,
    timestamp: new Date(),
    flags,
    summary,
    characters,
    resolved: false,
  }
}

/**
 * Find relevant consequence logs for context
 */
export function findRelevantConsequences(
  logs: ConsequenceLog[],
  currentCharacters: string[],
  currentLocation: string
): ConsequenceLog[] {
  return logs.filter(log => {
    // Not resolved yet
    if (log.resolved) return false

    // Involves current characters
    if (log.characters.some(c => currentCharacters.includes(c))) {
      return true
    }

    // Has location-related flags
    if (log.flags.some(f => f.includes(currentLocation))) {
      return true
    }

    // Recent and unresolved (within last 5 game days simulated)
    const daysSince = (Date.now() - log.timestamp.getTime()) / (1000 * 60 * 60 * 24)
    if (daysSince < 1 && !log.resolved) {
      return true
    }

    return false
  })
}

/**
 * Get appropriate category based on market conditions and player state
 */
export function suggestSituationCategory(
  session: GameSession,
  marketData: RealMarketData
): GeneratedSituation['category'] {
  const roll = Math.random()
  const { portfolio, traits } = session.player
  const progressPercent = (portfolio.totalValue / session.goal.targetAmount) * 100

  // Weight categories based on context
  let weights = {
    power_play: 15,
    lifestyle: 15,
    insider: 25,
    romance: 10,
    crisis: 20,
    recovery: 10,
    market: 5,
    random: 0,
  }

  // Adjust for progress
  if (progressPercent >= 70) {
    weights.lifestyle += 10
    weights.power_play += 10
  } else if (progressPercent <= 30) {
    weights.insider += 10
    weights.crisis += 10
  }

  // Adjust for market
  if (marketData.marketSentiment === 'extreme_fear') {
    weights.crisis += 15
    weights.recovery += 10
  } else if (marketData.marketSentiment === 'extreme_greed') {
    weights.lifestyle += 15
    weights.power_play += 10
  }

  // Adjust for traits
  if (traits.degenScore >= 70) {
    weights.insider += 10
  }
  if (traits.trustScore >= 70) {
    weights.romance += 10
  }

  // Calculate total and pick
  const total = Object.values(weights).reduce((a, b) => a + b, 0)
  let threshold = roll * total

  for (const [category, weight] of Object.entries(weights)) {
    threshold -= weight
    if (threshold <= 0) {
      return category as GeneratedSituation['category']
    }
  }

  return 'random'
}
